/**
 * PLAT-366 (Shell 4): the top bar's search, on Foundation's CommandPalette.
 * It replaces GlobalSearch (Push #17) and keeps what it did:
 *
 * - the same request: `fetchSearch(q.trim(), scope)`, i.e.
 *   `GET /api/search?q=…&scope=user|admin`, 200ms after the last keystroke;
 * - the same rows in the same order: Recent (only with no query), then the
 *   role-filtered Pages and Actions from shellNav.ts (at most 8 each, matched on
 *   title, subtitle or keyword), then the record groups the API returns;
 * - the same targets: a record or Page navigates and is remembered as Recent;
 *   an Action fires (Buy credits, Switch to admin mode, Sign out).
 *
 * The palette is fed rows already matched here, so Foundation shows them as
 * given (`external`) instead of matching them again on its own terms. With no
 * query it shows its non-external items, which is the Recent, Pages and
 * Actions menu GlobalSearch showed.
 *
 * Foundation owns the dialog: Escape and Enter close it and focus goes back to
 * whatever had it when it opened (0.85.12, PLAT-392). AppShell owns whether it
 * is open: the top-bar search control, ⌘K / Ctrl+K (useCommandPalette) and the
 * chat rail's `olytic:global-search:open` event.
 */

import { type CommandItem, CommandPalette } from '@olytic/foundation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useSession } from '../hooks/useSession.tsx';
import {
  type AdminSearchResponse,
  ApiError,
  fetchSearch,
  type SearchResult,
  signOut,
  type UserSearchResponse,
} from '../lib/api.ts';
import { BrandLogo } from './BrandLogo.tsx';
import { useBuyCredits } from './BuyCreditsProvider.tsx';
import { ObjectIcon, type ObjectKind } from './ObjectIcon.tsx';
import { type PaletteCommand, paletteCommandsFor } from './shellNav.ts';

const RECENT_KEY = 'olytic-search:recent';
const RECENT_MAX = 5;

interface RecentEntry {
  title: string;
  subtitle: string;
  to: string;
  kind: SearchResult['kind'];
  vendor_slug?: string;
}

function loadRecent(): RecentEntry[] {
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    if (raw === null) return [];
    const parsed = JSON.parse(raw) as RecentEntry[];
    return Array.isArray(parsed) ? parsed.slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

function pushRecent(entry: RecentEntry): void {
  try {
    // De-dupe by `to`; the new entry goes first.
    const next = [entry, ...loadRecent().filter((e) => e.to !== entry.to)].slice(0, RECENT_MAX);
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // Storage unavailable: nothing is remembered.
  }
}

/** One palette row: a record, a Recent entry, a Page, or an Action. */
export interface PaletteRow extends RecentEntry {
  groupLabel: string;
  keywords?: readonly string[];
  action?: PaletteCommand['action'];
}

/** Whether `q` (lowercase) is in a command's title, subtitle or keywords. */
const matches = (row: PaletteRow, q: string) =>
  [row.title, row.subtitle, ...(row.keywords ?? [])].some((t) => t.toLowerCase().includes(q));

/**
 * The rows GlobalSearch listed, in its order: with no response yet, Recent
 * (only for an empty query), Pages and Actions; with one, Pages, Actions and
 * then the record groups for its scope.
 */
export function paletteRows(
  commands: readonly PaletteCommand[],
  query: string,
  data: UserSearchResponse | AdminSearchResponse | null,
  recent: readonly RecentEntry[],
): PaletteRow[] {
  const q = query.trim().toLowerCase();
  const statics = commands
    .map<PaletteRow>((c) => ({
      title: c.title,
      subtitle: c.subtitle,
      to: c.to ?? '',
      kind: c.kind,
      groupLabel: c.groupLabel,
      ...(c.keywords !== undefined ? { keywords: c.keywords } : {}),
      ...(c.action !== undefined ? { action: c.action } : {}),
    }))
    .filter((r) => q === '' || matches(r, q));
  const pages = statics.filter((r) => r.groupLabel === 'Pages').slice(0, 8);
  const actions = statics.filter((r) => r.groupLabel === 'Actions').slice(0, 8);
  if (data === null) {
    const recentRows = q === '' ? recent.map((r) => ({ ...r, groupLabel: 'Recent' })) : [];
    return [...recentRows, ...pages, ...actions];
  }
  // D-167: Models → Connectors → Prompts.
  const groups: Array<[string, SearchResult[]]> =
    data.scope === 'user'
      ? [
          ['My models', data.my_models],
          ['My connectors', data.my_connectors],
          ['My prompts', data.my_prompts],
          ['Chat sessions', data.chat_sessions],
          ['Library documents', data.documents],
          ['Notifications', data.notifications],
          ['Recent activity', data.dispatches],
        ]
      : [
          ['Users', data.users],
          ['Permission sets', data.permission_sets],
          ['Models', data.models],
          ['Connectors', data.connectors],
          ['Prompts', data.prompts],
          ['Activity', data.activity],
        ];
  const records = groups.flatMap(([groupLabel, rows]) =>
    rows.map<PaletteRow>((r) => ({
      title: r.title,
      subtitle: r.subtitle,
      to: r.to,
      kind: r.kind,
      ...(r.vendor_slug !== undefined ? { vendor_slug: r.vendor_slug } : {}),
      groupLabel,
    })),
  );
  return [...pages, ...actions, ...records];
}

const KIND_ICON: Record<SearchResult['kind'], ObjectKind> = {
  connector: 'connectors',
  prompt: 'prompts',
  model: 'models',
  dispatch: 'activity',
  catalog_connector: 'catalog',
  catalog_prompt: 'catalog',
  catalog_model: 'catalog',
  user: 'people',
  permission_set: 'permission-sets',
  activity: 'activity',
  request: 'inbox',
  chat_session: 'chat',
  document: 'document',
  notification: 'notification',
};

const LOGO_KINDS = new Set(['connector', 'model', 'catalog_connector', 'catalog_model']);

/** The vendor logo for a connector or model row with a vendor, else the kind's icon. */
function RowIcon({ row }: { row: PaletteRow }) {
  if (row.vendor_slug !== undefined && LOGO_KINDS.has(row.kind)) {
    return <BrandLogo slug={row.vendor_slug} name={row.title} size="sm" />;
  }
  return <ObjectIcon kind={KIND_ICON[row.kind]} size="sm" />;
}

interface SearchPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scope: 'user' | 'admin';
}

export function SearchPalette({ open, onOpenChange, scope }: SearchPaletteProps) {
  const navigate = useNavigate();
  const { user, refresh, broadcastSignOut } = useSession();
  const { open: openBuyCredits } = useBuyCredits();
  const [query, setQuery] = useState('');
  const [data, setData] = useState<UserSearchResponse | AdminSearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Buy credits opens its own dialog, which keeps focus instead of the opener.
  const keepFocus = useRef(false);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setData(null);
    setError(null);
  }, [open]);

  // Debounced: 200ms here, plus the server's 30s cache.
  useEffect(() => {
    if (!open) return;
    const trimmed = query.trim();
    if (trimmed === '') {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const handle = setTimeout(() => {
      fetchSearch(trimmed, scope)
        .then((res) => {
          if (!cancelled) setData(res);
        })
        .catch((err: unknown) => {
          if (!cancelled) setError(err instanceof ApiError ? err.message : 'Search failed.');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query, open, scope]);

  const fire = useCallback(
    async (action: NonNullable<PaletteCommand['action']>) => {
      if (action === 'buy_credits') {
        keepFocus.current = true;
        openBuyCredits('proactive');
      } else if (action === 'toggle_admin') {
        navigate(scope === 'admin' ? '/home' : '/admin');
      } else {
        try {
          await signOut();
        } catch {
          // refresh() below lands on /sign-in either way.
        }
        broadcastSignOut();
        refresh();
        navigate('/sign-in');
      }
    },
    [openBuyCredits, navigate, scope, broadcastSignOut, refresh],
  );

  // The palette has closed by the time a row's onSelect runs.
  const select = useCallback(
    (row: PaletteRow) => {
      if (row.action !== undefined) {
        void fire(row.action);
        return;
      }
      if (row.to === '') return;
      pushRecent({
        title: row.title,
        subtitle: row.subtitle,
        to: row.to,
        kind: row.kind,
        ...(row.vendor_slug !== undefined ? { vendor_slug: row.vendor_slug } : {}),
      });
      navigate(row.to);
    },
    [fire, navigate],
  );

  const commands = useMemo(() => paletteCommandsFor(user?.role ?? 'user'), [user?.role]);
  const trimmed = query.trim();
  const items = useMemo<CommandItem[]>(() => {
    // An error replaces the rows, as GlobalSearch showed it.
    if (error !== null) return [];
    const recent = trimmed === '' && data === null ? loadRecent() : [];
    return paletteRows(commands, trimmed, data, recent).map((row, i) => ({
      id: `${row.groupLabel}-${i}`,
      label: row.title,
      group: row.groupLabel,
      icon: <RowIcon row={row} />,
      keywords: [row.subtitle, ...(row.keywords ?? [])].join(' '),
      ...(trimmed !== '' ? { external: true } : {}),
      onSelect: () => select(row),
    }));
  }, [error, trimmed, data, commands, select]);

  return (
    <CommandPalette
      open={open}
      onOpenChange={onOpenChange}
      items={items}
      onQueryChange={setQuery}
      loading={loading}
      title="Global search"
      placeholder={
        scope === 'admin' ? 'Search users, connectors, activity…' : 'Search your toolkit + market…'
      }
      emptyLabel={
        error ?? (trimmed === '' ? 'Start typing to search.' : `No results for "${trimmed}".`)
      }
      onCloseAutoFocus={(event) => {
        if (!keepFocus.current) return;
        keepFocus.current = false;
        event.preventDefault();
      }}
    />
  );
}
